/** Formatting and animating a points balance.
 *
 * Apart from `AnimatedPoints.tsx` so that file exports only a component: a module that
 * mixes the two defeats Fast Refresh, which silently stops preserving state during
 * development and is the sort of thing you waste an afternoon on before noticing.
 */

import { useEffect, useRef, useState } from 'react'

/** Swiss grouping, as the design writes it: 1'250, with a typographic apostrophe. */
export function formatPoints(value: number): string {
  return Math.round(value).toLocaleString('de-CH').replace(/[’'`´]/g, '’')
}

const DURATION_MS = 700

/** Count from the previous balance to the new one instead of snapping.
 *
 * The designer's note: "After a reward is handed out or any other WebX action that
 * affects points value -> we need the numbers to animate, to the new total."
 *
 * Not decoration. The cashier hands over a reward and the customer watches their balance
 * change; a number that simply swaps leaves both unsure whether it changed at all, and
 * the whole point of showing a balance at the till is that the customer can see what it
 * cost them.
 */
export function useAnimatedPoints(target: number): number {
  const [shown, setShown] = useState(target)
  const fromRef = useRef(target)
  const frameRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    const from = fromRef.current
    if (from === target) return

    // Respect the platform setting rather than animating regardless. A till in a busy
    // service is exactly where motion sensitivity matters.
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (reduced) {
      fromRef.current = target
      setShown(target)
      return
    }

    const started = performance.now()
    const step = (now: number) => {
      const progress = Math.min(1, (now - started) / DURATION_MS)
      const eased = 1 - Math.pow(1 - progress, 3)
      setShown(from + (target - from) * eased)
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(step)
      } else {
        // Land on the exact integer: an eased float would otherwise leave the balance
        // one point short of the truth.
        fromRef.current = target
        setShown(target)
      }
    }
    frameRef.current = requestAnimationFrame(step)

    return () => {
      if (frameRef.current !== undefined) cancelAnimationFrame(frameRef.current)
      // Whatever we had reached becomes the start of the next run, so an interrupted
      // animation continues from where the customer last saw it.
      fromRef.current = shown
    }
    // `shown` is deliberately not a dependency - including it would restart the
    // animation on every frame it sets.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target])

  return shown
}
